"use client";

import Image from "next/image";
import {
	Camera,
	MapPin,
	ArrowRight,
	CheckCircle2,
	Clock,
	Image as ImageIcon,
	Zap,
} from "lucide-react";
import { motion } from "motion/react";
import Link from "next/link";
import { LIEN_VEST_CONTACT_SUPPORT_PATH } from "@/components/imagery/ImagerySupportFab";

/** Local copies of Figma showcase photos — reliable load, full frame in grid */
const SHOWCASE_IMAGES = [
	{
		src: "/imagery/showcase/01.jpg",
		alt: "Modern residential property exterior",
	},
	{
		src: "/imagery/showcase/02.jpg",
		alt: "Commercial building — The Factory",
	},
	{ src: "/imagery/showcase/03.jpg", alt: "Property with pool and grounds" },
	{ src: "/imagery/showcase/04.jpg", alt: "High-rise facade" },
] as const;

const IVUEIT_LOGO_GIF =
	"https://ivueit.com/wp-content/uploads/2020/07/logo_ivueit.png";

export interface ImageryRequestPageProps {
	onSubmit: () => void;
	onCheckIn?: () => void;
	/** Tighter typography/spacing when signed-in; landing omits top brand row for everyone (see `/imagery`). */
	chrome?: "guest" | "loggedIn";
}

export function ImageryRequestPage({
	onSubmit,
	onCheckIn,
	chrome = "guest",
}: ImageryRequestPageProps) {
	/** At least full viewport height; document scrolls when content is taller (short wide viewports). */
	const fillShell = "min-h-dvh w-full";

	return (
		<div className={`grid grid-cols-1 lg:grid-cols-2 ${fillShell}`}>
			{/* Left Side - Hero & Showcase (hidden below `lg`; stacks + shrinks in-column above that) */}
			<div
				className={`relative hidden min-h-0 flex-col justify-center overflow-x-hidden bg-[#2a3f38] bg-gradient-to-br from-[#16251c] to-[#0f1b13] lg:flex lg:min-h-dvh ${
					chrome === "loggedIn"
						? "p-[clamp(0.75rem,3vmin,2.5rem)] sm:p-[clamp(0.875rem,3.2vmin,2.75rem)] lg:p-[clamp(1rem,3.5vmin,2.75rem)]"
						: "p-[clamp(0.875rem,3.5vmin,3rem)] lg:p-[clamp(1rem,4vmin,3rem)]"
				}`}
			>
				{/* Background Pattern — visible in gutters between tiles */}
				<div className="pointer-events-none absolute inset-0 opacity-[0.14]">
					<div
						className="absolute inset-0"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23A7FF5E' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
						}}
					/>
				</div>

				<div className="relative z-10 mx-auto flex min-h-0 w-full min-w-0 max-w-full flex-col justify-center overflow-x-hidden lg:max-w-[min(36rem,100%)]">
					<div className="mb-[clamp(0.35rem,2vmin,1.25rem)] flex w-full justify-end max-sm:justify-start">
						<a
							href="https://ivueit.com"
							target="_blank"
							rel="noopener noreferrer"
							className="flex shrink-0 flex-col items-end text-right max-sm:items-start"
						>
							<span className="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-white/45">
								Powered by
							</span>
							{/* eslint-disable-next-line @next/next/no-img-element -- external animated GIF */}
							<img
								src={IVUEIT_LOGO_GIF}
								alt="iVueit"
								className="h-7 w-auto max-h-9 object-contain object-right sm:h-8"
								width={140}
								height={36}
							/>
						</a>
					</div>

					{/* Hero Content */}
					<div
						className={
							chrome === "loggedIn"
								? "mb-[clamp(0.5rem,2.2vmin,1.35rem)]"
								: "mb-[clamp(0.65rem,2.8vmin,2rem)]"
						}
					>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
						>
							<h2
								className={`mb-[clamp(0.35rem,1.5vmin,1rem)] font-bold leading-[1.15] text-white ${
									chrome === "loggedIn"
										? "text-[clamp(1.35rem,4.5vmin,2.5rem)] sm:text-[clamp(1.5rem,4.8vmin,2.75rem)]"
										: "text-[clamp(1.5rem,5vmin,3.25rem)] sm:text-[clamp(1.65rem,5.5vmin,3.5rem)]"
								}`}
							>
								Real Property Images
								<br />
								<span className="text-[#A7FF5E]">
									Across the US
								</span>
							</h2>
							<p
								className={`text-[clamp(0.8125rem,2vmin,1.125rem)] leading-relaxed text-[#edffde]/80 ${
									chrome === "loggedIn"
										? "mb-[clamp(0.35rem,1.8vmin,1rem)]"
										: "mb-[clamp(0.5rem,2.2vmin,1.5rem)]"
								}`}
							>
								Professional, ground-level photographs of any
								property. No satellite views, no stock images
								just real photos delivered in 24-48 hours.
							</p>
						</motion.div>
					</div>

					{/* Image Grid — 2×2, gutters show pattern; local assets */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className={`mx-auto grid w-full max-w-full grid-cols-2 gap-[clamp(0.3rem,1.5vmin,0.85rem)] lg:gap-[clamp(0.35rem,1.4vmin,1rem)] ${
							chrome === "loggedIn"
								? "mb-[clamp(0.35rem,2vmin,1rem)]"
								: "mb-[clamp(0.5rem,2.5vmin,1.5rem)]"
						}`}
					>
						{SHOWCASE_IMAGES.map(({ src, alt }, idx) => (
							<motion.div
								key={src}
								initial={{ opacity: 0, scale: 0.96 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{
									duration: 0.4,
									delay: 0.3 + idx * 0.08,
								}}
								className="group relative aspect-[4/3] w-full overflow-hidden rounded-[clamp(0.75rem,2vmin,1.25rem)] border-2 border-white/30 bg-[#1a2822] shadow-lg"
							>
								<Image
									src={src}
									alt={alt}
									fill
									className="object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
									sizes="(max-width: 1024px) 45vw, 22vw"
									priority={idx < 2}
									quality={90}
								/>
								<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
							</motion.div>
						))}
					</motion.div>

					{/* Features */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.6 }}
						className="grid min-w-0 grid-cols-3 gap-[clamp(0.35rem,2vmin,1rem)] [@media(max-height:720px)]:gap-2"
					>
						{[
							{ icon: Camera, label: "Pro Quality" },
							{ icon: MapPin, label: "US Coverage" },
							{ icon: Clock, label: "24-48 Hours" },
						].map((feature, idx) => (
							<div
								key={idx}
								className="flex flex-col items-center gap-[clamp(0.25rem,1.2vmin,0.5rem)] rounded-lg border border-[#A7FF5E]/20 bg-[#A7FF5E]/10 p-[clamp(0.35rem,1.5vmin,0.75rem)] text-center [@media(max-height:720px)]:p-2"
							>
								<feature.icon className="h-[clamp(1rem,2.8vmin,1.35rem)] w-[clamp(1rem,2.8vmin,1.35rem)] shrink-0 text-[#A7FF5E]" />
								<p className="text-[clamp(0.65rem,1.7vmin,0.875rem)] font-semibold leading-tight text-white">
									{feature.label}
								</p>
							</div>
						))}
					</motion.div>
				</div>
			</div>

			{/* Right Side - Information & CTA */}
			<div
				className={`flex min-h-0 w-full min-w-0 flex-col items-center justify-center overflow-x-hidden bg-gray-50 py-8 text-center lg:min-h-dvh lg:py-0 ${
					chrome === "loggedIn"
						? "p-[clamp(0.75rem,3vmin,2.5rem)] sm:p-[clamp(0.875rem,3.2vmin,2.75rem)] lg:p-[clamp(1rem,3.5vmin,2.75rem)]"
						: "p-[clamp(0.875rem,3.5vmin,3rem)] lg:p-[clamp(1rem,4vmin,3rem)]"
				} max-lg:px-5 sm:max-lg:px-8`}
			>
				{/* Width from globals.css `.imagery-landing-cta-inner` (775px tablet → ~20–22rem column). */}
				<div className="imagery-landing-cta-inner flex min-h-0 shrink-0 flex-col justify-center">
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						className="min-w-0"
					>
						{/* Single stacked layout: one primary card. Desktop split: wrapper is `display:contents` (no extra box). */}
						<div className="max-lg:rounded-2xl max-lg:bg-white max-lg:p-6 max-lg:shadow-[0_24px_56px_-16px_rgba(22,37,28,0.22),0_10px_32px_-10px_rgba(0,0,0,0.14)] max-lg:ring-1 max-lg:ring-gray-900/[0.07] sm:max-lg:p-8 lg:contents">
							<div
								className={
									chrome === "loggedIn"
										? "space-y-[clamp(0.65rem,2.5vmin,1.35rem)] max-lg:space-y-7 [@media(max-height:720px)]:space-y-3 [@media(max-height:720px)]:max-lg:space-y-5"
										: "space-y-[clamp(0.85rem,3vmin,2rem)] max-lg:space-y-8 [@media(max-height:720px)]:space-y-4 [@media(max-height:720px)]:max-lg:space-y-5"
								}
							>
								{/* Header */}
								<div className="flex flex-col items-center max-lg:pb-1">
									<div className="mb-[clamp(0.35rem,1.8vmin,1rem)] inline-flex items-center gap-2 rounded-lg border border-[#A7FF5E]/30 bg-[#A7FF5E]/20 px-[clamp(0.5rem,1.8vmin,0.85rem)] py-[clamp(0.25rem,1vmin,0.45rem)]">
										<Camera className="h-[clamp(0.85rem,2.4vmin,1.1rem)] w-[clamp(0.85rem,2.4vmin,1.1rem)] text-[#16251c]" />
										<span className="text-[clamp(0.7rem,2vmin,0.875rem)] font-semibold text-[#16251c]">
											Professional Photography
										</span>
									</div>
									<h2 className="mb-[clamp(0.35rem,1.5vmin,0.85rem)] w-full text-[clamp(1.35rem,4vmin,2.25rem)] font-bold leading-tight text-[#16251c] sm:text-[clamp(1.5rem,4.2vmin,2.5rem)]">
										Property Photos On-Demand
									</h2>
									<p className="w-full text-[clamp(0.875rem,2.2vmin,1.125rem)] text-gray-600">
										Get professional photographs of any
										property in the United States, delivered
										fast.
									</p>
								</div>

								{/* What You Get */}
								<div
									className={`max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:p-0 max-lg:shadow-none max-lg:ring-0 max-lg:outline-none lg:rounded-xl lg:border lg:border-gray-200 lg:bg-white lg:shadow-md [@media(max-height:720px)]:lg:p-3 ${
										chrome === "loggedIn"
											? "lg:p-[clamp(0.75rem,2.4vmin,1.35rem)]"
											: "lg:p-[clamp(0.85rem,2.6vmin,1.5rem)]"
									}`}
								>
									<h3 className="mb-[clamp(0.35rem,2vmin,1rem)] flex items-center justify-center gap-2 text-[clamp(0.9rem,2.4vmin,1.125rem)] font-bold text-gray-900">
										<ImageIcon className="h-[clamp(1rem,2.6vmin,1.35rem)] w-[clamp(1rem,2.6vmin,1.35rem)] shrink-0 text-[#16251c]" />
										What You&apos;ll Receive
									</h3>
									<div className="mx-auto flex w-fit max-w-full flex-col items-start space-y-[clamp(0.35rem,1.5vmin,0.75rem)] max-lg:space-y-3">
										{[
											"Multiple angles (front, side)",
											"High-resolution professional-grade images",
											"Fast 24-48 hour delivery with notification",
										].map((item, idx) => (
											<div
												key={idx}
												className="flex items-start justify-start gap-2 text-left"
											>
												<CheckCircle2 className="mt-0.5 h-[clamp(1rem,2.6vmin,1.25rem)] w-[clamp(1rem,2.6vmin,1.25rem)] shrink-0 text-[#A7FF5E]" />
												<p className="text-pretty text-[clamp(0.8rem,2vmin,1rem)] text-gray-700">
													{item}
												</p>
											</div>
										))}
									</div>
								</div>

								{/* How It Works */}
								<div
									className={`max-lg:rounded-xl max-lg:border max-lg:border-[#A7FF5E]/25 max-lg:bg-gradient-to-br max-lg:from-[#A7FF5E]/10 max-lg:to-[#cbf7a6]/14 max-lg:p-5 max-lg:shadow-none max-lg:ring-0 lg:rounded-xl lg:border lg:border-[#A7FF5E]/30 lg:bg-gradient-to-br lg:from-[#A7FF5E]/10 lg:to-[#cbf7a6]/10 lg:shadow-sm [@media(max-height:720px)]:lg:p-3 ${
										chrome === "loggedIn"
											? "lg:p-[clamp(0.75rem,2.4vmin,1.35rem)]"
											: "lg:p-[clamp(0.85rem,2.6vmin,1.5rem)]"
									}`}
								>
									<h3 className="mb-[clamp(0.35rem,2vmin,1rem)] flex items-center justify-center gap-2 text-[clamp(0.9rem,2.4vmin,1.125rem)] font-bold text-[#16251c]">
										<Zap className="h-[clamp(1rem,2.6vmin,1.35rem)] w-[clamp(1rem,2.6vmin,1.35rem)] shrink-0" />
										Simple Process
									</h3>
									<div className="mx-auto grid w-max max-w-full grid-cols-2 justify-items-center gap-x-4 gap-y-[clamp(0.35rem,1.5vmin,0.75rem)] max-lg:gap-y-4 sm:gap-x-6 [@media(max-height:720px)]:gap-x-2 [@media(max-height:720px)]:gap-y-2">
										{[
											{
												num: "1",
												text: "Submit address",
											},
											{
												num: "2",
												text: "Photographer assigned",
											},
											{
												num: "3",
												text: "Property visit",
											},
											{
												num: "4",
												text: "Photos delivered",
											},
										].map((step) => (
											<div
												key={step.num}
												className="flex flex-col items-center gap-1.5 text-center"
											>
												<div className="flex h-[clamp(1.25rem,3vmin,1.5rem)] w-[clamp(1.25rem,3vmin,1.5rem)] shrink-0 items-center justify-center rounded-md bg-[#16251c] text-[clamp(0.6rem,1.8vmin,0.75rem)] font-bold text-[#A7FF5E]">
													{step.num}
												</div>
												<p className="text-pretty text-[clamp(0.7rem,1.9vmin,0.875rem)] font-medium leading-snug text-[#16251c]">
													{step.text}
												</p>
											</div>
										))}
									</div>
								</div>

								{/* CTA */}
								<div className="space-y-[clamp(0.45rem,1.6vmin,0.75rem)] max-lg:space-y-4">
									<button
										type="button"
										onClick={onSubmit}
										className={`group flex w-full items-center justify-center gap-[clamp(0.35rem,1.5vmin,0.85rem)] rounded-xl bg-gradient-to-r from-[#16251c] to-[#0f1b13] py-[clamp(0.6rem,2.2vmin,1rem)] text-[clamp(0.85rem,2.3vmin,1.125rem)] font-bold text-white shadow-lg transition-all hover:from-[#0f1b13] hover:to-[#16251c] hover:shadow-xl`}
									>
										<Camera className="h-[clamp(1rem,2.6vmin,1.35rem)] w-[clamp(1rem,2.6vmin,1.35rem)] shrink-0" />
										Request photos now
										<ArrowRight className="h-[clamp(1rem,2.6vmin,1.35rem)] w-[clamp(1rem,2.6vmin,1.35rem)] shrink-0 transition-transform group-hover:translate-x-1" />
									</button>

									{onCheckIn ? (
										<button
											type="button"
											onClick={onCheckIn}
											className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white py-[clamp(0.55rem,2vmin,0.85rem)] text-[clamp(0.8rem,2.1vmin,1rem)] font-semibold text-gray-700 transition-all hover:border-[#A7FF5E] hover:bg-gray-50"
										>
											<CheckCircle2 className="h-[clamp(1rem,2.6vmin,1.35rem)] w-[clamp(1rem,2.6vmin,1.35rem)] shrink-0" />
											Check Request Status
										</button>
									) : null}

									<Link
										href={LIEN_VEST_CONTACT_SUPPORT_PATH}
										className="block w-full text-center text-[clamp(0.75rem,2vmin,0.9375rem)] font-semibold text-[#16251c]/85 underline-offset-[0.22em] transition-colors hover:text-[#16251c] hover:underline"
									>
										Contact support
									</Link>
								</div>

								{/* Stats */}
								<div className="grid w-full grid-cols-3 gap-2 border-t border-gray-200 pt-[clamp(0.35rem,1.8vmin,1rem)] max-lg:pt-6 max-lg:border-gray-200/80 sm:gap-3 [@media(max-height:720px)]:pt-3 [@media(max-height:720px)]:max-lg:pt-4">
									{[
										{ value: "50", label: "States" },
										{ value: "24-48h", label: "Delivery" },
										{ value: "100%", label: "Real Photos" },
									].map((stat, idx) => (
										<div key={idx} className="text-center">
											<p className="text-[clamp(1.125rem,3.2vmin,1.5rem)] font-bold text-[#16251c]">
												{stat.value}
											</p>
											<p className="text-[clamp(0.65rem,1.8vmin,0.875rem)] text-gray-600">
												{stat.label}
											</p>
										</div>
									))}
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</div>
	);
}
